<template>
    <div>
        <loading v-if="loading"></loading>
        <template v-else>
            <div class="list-group">
                <template v-for="group in groups">
                    <div class="list-group-item bg-white" :key="group.id">
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="font-weight-bold">
                                {{ group.name }}
                            </div>
                            <div>
                                <span class="small text-muted mr-2">ID: {{ group.id }}</span>
                                <b-button v-if="showUsers !== group.id" variant="outline" class="btn-sm" @click="toggleShowUsers(group.id)">Show Users</b-button>
                                <b-button variant="outline" class="btn-sm" @click="openEdit(group.id)"><b-icon icon="pencil-square"></b-icon></b-button>
                                <b-button variant="outline" @click="askDelete(group)" class="btn-sm"><b-icon icon="trash"></b-icon></b-button>
                            </div>
                        </div>
                        <p class="mb-1 text-muted">{{ group.description }}</p>

                        <div v-if="editId === group.id">
                            <hr />
                            <validation-observer ref="observer" v-slot="{ passes }">
                                <form @submit.prevent="passes(() => editGroup(group))" style="max-width: 500px;">
                                    <validation-provider name="name" rules="min:2|required" v-slot="{ errors }">
                                        <b-form-group label="Name">
                                            <b-form-input v-model="group.name"></b-form-input>
                                            <span class="validation">{{ errors[0] || formErrors.editGroupName }}</span> <!-- Display validation error -->
                                        </b-form-group>
                                    </validation-provider>

                                    <validation-provider name="description" rules="" v-slot="{ errors }">
                                        <b-form-group label="Description">
                                            <b-form-textarea v-model="group.description"></b-form-textarea>
                                            <span class="validation">{{ errors[0] || formErrors.editGroupDescription }}</span> <!-- Display validation error -->
                                        </b-form-group>
                                    </validation-provider>

                                    <b-button variant="outline" class="mt-2 mr-2" @click='editId = null'>Cancel</b-button>
                                    <b-button type="submit" variant="primary" class="mt-2">Save</b-button>
                                </form>
                            </validation-observer>
                        </div>

                        <b-card v-if="showUsers === group.id" class="mt-2">
                            <h5>Users in this group</h5>
                            <loading v-if="loadingUsers"></loading>
                            <template v-else>
                                <ul v-if="group.users && group.users.length" class="list-group mb-2">
                                    <li v-for="user in group.users" :key="user.id" class="list-group-item d-flex justify-content-between align-items-center">
                                        {{ user.name }}
                                        <b-button variant="outline-danger" size="sm" @click="removeUserFromGroup(group.id, user.id)">Remove</b-button>
                                    </li>
                                </ul>
                                <p v-else>No users in this group.</p>
                            </template>
                            <b-form @submit.prevent="addUserToGroup(group.id)">
                                <b-form-group label="Add User">
                                    <v-select
                                        v-model="selectedUser"
                                        label="name"
                                        :options="users"
                                        @search="onSearch"
                                        :filterable="false"
                                        :loading="isLoading"
                                    >
                                        <template #no-options="{ search, searching }">
                                            <template v-if="searching">
                                                No results found for <em>{{ search }}</em>.
                                            </template>
                                            <em v-else>Start typing to search for a user</em>
                                        </template>
                                        <template #option="option">
                                            <div>{{ option.name }} ({{ option.email }})</div>
                                        </template>
                                    </v-select>
                                </b-form-group>
                                <div class="d-flex justify-content-between">
                                    <b-button type="submit" variant="primary" :disabled="!selectedUser">Add User</b-button>
                                    <b-button @click="showUsers = null" variant="outline" class="mt-2">Close Users</b-button>
                                </div>

                            </b-form>
                        </b-card>
                    </div>
                </template>
            </div>

            <b-button variant="outline" class="mt-2" @click="isNewFormOpen = true">
                <b-icon icon="plus-circle" class="text-primary"></b-icon> Add new group
            </b-button>

            <div v-if="isNewFormOpen">
                <hr />
                <validation-observer ref="observer" v-slot="{ passes }">
                    <b-form @submit.prevent="passes(createGroup)" style="max-width: 500px;">
                        <b-form-group label="Name" label-for="new-name" class="mb-2">
                            <validation-provider name="name" rules="required|min:2|max:60" v-slot="{ errors }">
                                <b-form-input id="new-name" v-model="newGroupName"></b-form-input>
                                <span class="validation">{{ errors[0] || formErrors.newGroupName }}</span> <!-- Display validation error -->
                            </validation-provider>
                        </b-form-group>

                        <b-form-group label="Description" label-for="new-description" class="mb-2">
                            <validation-provider name="description" rules="" v-slot="{ errors }">
                                <b-form-textarea id="new-description" v-model="newGroupDescription"></b-form-textarea>
                                <span class="validation">{{ errors[0] || formErrors.newGroupDescription }}</span> <!-- Display validation error -->
                            </validation-provider>
                        </b-form-group>

                        <div class="d-flex justify-content-between mt-3">
                            <b-button variant="outline" @click="isNewFormOpen = false">
                                Cancel
                            </b-button>
                            <b-button type="submit" variant="primary">Create</b-button>
                        </div>
                    </b-form>
                </validation-observer>
            </div>

            <b-modal
                id="delete-group-modal"
                title="Delete group"
                ok-title="Delete"
                cancel-variant="outline"
                ok-variant="danger"
                @ok="handleDeleteOk">
                <div v-if="groupToDelete">
                    <p>Are you sure you want to delete the group "{{ groupToDelete.name }}"?</p>
                    <p>This action cannot be undone.</p>
                </div>
            </b-modal>
        </template>
    </div>
</template>

<script>
import NotificationPop from "../mixins/NotificationPop";
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Loading
    from "./Loading";

export default {
    components: {
        vSelect,
        Loading
    },
    mixins: [NotificationPop],
    data() {
        return {
            groups: [],
            groupToDelete: null,
            editId: null,
            showUsers: null,
            newGroupName: '',
            newGroupDescription: '',
            isNewFormOpen: false,
            selectedUser: null,
            users: [],
            isLoading: false,
            loading: true,
            loadingUsers: false,
            formErrors: {
                newGroupName: null,
                newGroupDescription: null,
                editGroupName: null,
                editGroupDescription: null,
            },
        };
    },
    methods: {
        fetchGroups() {
            this.loading = true;
            this.$http.get(`/http_api/groups`)
                .then((response) => {
                    this.groups = response.data.data;
                    this.loading = false;
                })
                .catch((error) => {
                    this.popNotification('Failed to fetch groups.', 'danger');
                    this.loading = false;
                });
        },
        editGroup(group) {
            this.clearFormErrors();
            this.$http.put(`/http_api/groups/${group.id}`, {
                name: group.name,
                description: group.description
            })
                .then((response) => {
                    this.popNotification('Group updated successfully', 'success');
                    this.editId = null;
                    this.fetchGroups();
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        const errors = error.response.data.errors;
                        if (errors.name) {
                            this.formErrors.editGroupName = errors.name[0]; // Assign error message to the form field
                        }
                        if (errors.description) {
                            this.formErrors.editGroupDescription = errors.description[0]; // Assign error message to the form field
                        }
                    } else {
                        this.popNotification('Failed to update group.', 'danger');
                    }
                });
        },
        askDelete(group) {
            this.groupToDelete = group;
            this.$bvModal.show('delete-group-modal');
        },
        handleDeleteOk() {
            this.deleteGroup();
        },
        deleteGroup() {
            this.$http.delete(`/http_api/groups/${this.groupToDelete.id}`)
                .then((response) => {
                    this.groups = this.groups.filter(group => group.id !== this.groupToDelete.id);
                    this.groupToDelete = null;
                    this.popNotification('Group deleted successfully', 'success');
                })
                .catch((error) => {
                    this.popNotification('Failed to delete group.', 'danger');
                });
        },
        createGroup() {
            this.clearFormErrors();
            this.$http.post('/http_api/groups', {
                name: this.newGroupName,
                description: this.newGroupDescription,
            })
                .then((response) => {
                    this.groups.push(response.data.data);
                    this.newGroupName = '';
                    this.newGroupDescription = '';
                    this.isNewFormOpen = false;
                    this.popNotification('Group created successfully', 'success');
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        const errors = error.response.data.errors;
                        if (errors.name) {
                            this.formErrors.newGroupName = errors.name[0]; // Assign error message to the form field
                        }
                        if (errors.description) {
                            this.formErrors.newGroupDescription = errors.description[0]; // Assign error message to the form field
                        }
                    } else {
                        this.popNotification('Failed to create group.', 'danger');
                    }
                });
        },
        openEdit(groupId) {
            this.editId = groupId;
        },
        toggleShowUsers(groupId) {
            this.showUsers = groupId;
            this.loadGroupUsers(groupId);
        },
        loadGroupUsers(groupId) {
            this.loadingUsers = true;
            this.$http.get(`/http_api/groups/${groupId}/users`)
                .then((response) => {
                    const groupIndex = this.groups.findIndex(g => g.id === groupId);
                    if (groupIndex !== -1) {
                        this.$set(this.groups[groupIndex], 'users', response.data.data);
                    }
                    this.loadingUsers = false;
                })
                .catch((error) => {
                    this.popNotification('Failed to load users for this group.', 'danger');
                    this.loadingUsers = false;
                });
        },
        onSearch(search, loading) {
            if (search.length) {
                loading(true);
                this.isLoading = true;
                this.searchUsers(loading, search);
            } else {
                this.users = [];
            }
        },
        searchUsers(loading, search) {
            this.$http.get('/http_api/users/autocomplete', {
                params: { query: search }
            })
                .then(response => {
                    this.users = response.data;
                    loading(false);
                    this.isLoading = false;
                })
                .catch(error => {
                    console.error('Error fetching users', error);
                    loading(false);
                    this.isLoading = false;
                    this.popNotification('Failed to fetch users. Please try again.', 'danger');
                });
        },
        addUserToGroup(groupId) {
            if (!this.selectedUser) {
                this.popNotification('Please select a user to add.', 'warning');
                return;
            }
            this.$http.post(`/http_api/groups/${groupId}/users`, {
                user_id: this.selectedUser.id
            })
                .then((response) => {
                    this.popNotification('User added to group successfully', 'success');
                    this.loadGroupUsers(groupId);
                    this.selectedUser = null;
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.popNotification(error.response.data.message, 'warning');
                    } else {
                        this.popNotification('Failed to add user to group.', 'danger');
                    }
                });
        },
        removeUserFromGroup(groupId, userId) {
            this.$http.delete(`/http_api/groups/${groupId}/users`, {
                data: { user_id: userId }
            })
                .then((response) => {
                    this.popNotification('User removed from group successfully', 'success');
                    this.loadGroupUsers(groupId);
                })
                .catch((error) => {
                    this.popNotification('Failed to remove user from group.', 'danger');
                });
        },
        clearFormErrors() {
            this.formErrors.newGroupName = null;
            this.formErrors.newGroupDescription = null;
            this.formErrors.editGroupName = null;
            this.formErrors.editGroupDescription = null;
        },
    },
    mounted() {
        this.fetchGroups();
    },
};
</script>

<style scoped>
.validation {
    color: red;
    font-size: 0.8em;
}
</style>